import type {
  ProductVariant,
  DiscountCodeInfo,
  CustomQuery,
  Cart,
  InStoreMe,
  LineItem,
} from '~/types/commercetools';
import { useExpondoMiddleware } from '~/composables';
import { EXPONDO_MIDDLEWARE_ROUTES } from '~/constants/routes';

type CartResponse = {
  cart: Cart;
}

export function useCartApi() {
  const { makeCommercetoolsRequest } = useExpondoMiddleware();
  const load = async ({ customQuery }: { customQuery?: CustomQuery } = { customQuery: undefined }) => {
    const response = await makeCommercetoolsRequest<{ me: InStoreMe }>({
      endpoint: EXPONDO_MIDDLEWARE_ROUTES.CT.GET_ME,
      data: [
        { customer: false }, customQuery,
      ],
    });
    return response?.data?.me?.activeCart || null;
  };

  const addItem = async ({
    currentCart,
    product,
    quantity,
    supplyChannel,
    distributionChannel,
    customQuery,
  }: {
    currentCart: Cart;
    product: ProductVariant;
    quantity: number;
    supplyChannel?: string;
    distributionChannel?: string;
    customQuery?: CustomQuery;
  }): Promise<Maybe<Cart>> => {
    let cart: Maybe<Cart> = currentCart;
    if (!currentCart) {
      const createCartResponse = await makeCommercetoolsRequest<CartResponse>({
        endpoint: EXPONDO_MIDDLEWARE_ROUTES.CT.CREATE_CART,
        data: [{}, currentCart],
      });
      cart = createCartResponse?.data?.cart;
    }
    if (!cart) {
      throw new Error('Cart not found');
    }
    const addToCartResponse = await makeCommercetoolsRequest<CartResponse>({
      endpoint: EXPONDO_MIDDLEWARE_ROUTES.CT.ADD_TO_CART,
      data: [
        { id: cart.id, version: cart.version },
        { product, quantity, supplyChannel, distributionChannel },
        customQuery,
      ],
    });
    return addToCartResponse?.data?.cart;
  };

  const removeItem = async ({
    currentCart,
    product,
    customQuery,
  }: { currentCart: Cart; product: LineItem; customQuery?: CustomQuery; }) => {
    const removeFromCartResponse = await makeCommercetoolsRequest<CartResponse>({
      endpoint: EXPONDO_MIDDLEWARE_ROUTES.CT.REMOVE_FROM_CART,
      data: [
        {
          id: currentCart.id,
          version: currentCart.version,
        },
        product,
        customQuery,
      ],
    });
    return removeFromCartResponse?.data?.cart;
  };

  const updateItemQty = async ({
    currentCart,
    product,
    quantity,
    customQuery,
  }: {
    currentCart?: Cart;
    product: LineItem;
    quantity?: number;
    customQuery?: CustomQuery;
}) => {
    const updateCartQuantityResponse = await makeCommercetoolsRequest<CartResponse>({
      endpoint: EXPONDO_MIDDLEWARE_ROUTES.CT.UPDATE_CART_QUANTITY,
      data: [
        {
          id: currentCart?.id,
          version: currentCart?.version,
        },
        { ...product, quantity },
        customQuery,
      ],
    });
    return updateCartQuantityResponse?.data?.cart;
  };

  const clear = async ({ currentCart }: { currentCart: Cart }) => {
    const deleteCartResponse = await makeCommercetoolsRequest<CartResponse>({
      endpoint: EXPONDO_MIDDLEWARE_ROUTES.CT.DELETE_CART,
      data: [
        {
          id: currentCart.id,
          version: currentCart.version,
        },
      ],
    });
    return deleteCartResponse?.data?.cart;
  };

  const applyCoupon = async ({
    currentCart,
    couponCode,
    customQuery,
  }: {
    currentCart: Cart;
    couponCode: string;
    customQuery?: CustomQuery;
  }) => {
    const applyCartCouponResponse = await makeCommercetoolsRequest<CartResponse>({
      endpoint: EXPONDO_MIDDLEWARE_ROUTES.CT.APPLY_CART_COUPON,
      data: [
        {
          id: currentCart.id,
          version: currentCart.version,
        },
        couponCode,
        customQuery,
      ],
    });
    return {
      updatedCart: applyCartCouponResponse?.data?.cart,
      updatedCoupon: couponCode,
    };
  };

  const removeCoupon = async ({
    currentCart,
    couponCode,
    customQuery,
  }: { currentCart: Cart; couponCode: string; customQuery?: CustomQuery }) => {
    const couponId = currentCart?.discountCodes?.find(
      (d: DiscountCodeInfo) => d?.discountCode?.code === couponCode,
    )?.discountCode?.id;

    if (!couponId) {
      return { updatedCart: currentCart };
    }
    const removeCartCouponResponse = await makeCommercetoolsRequest<CartResponse>({
      endpoint: EXPONDO_MIDDLEWARE_ROUTES.CT.REMOVE_CART_COUPON,
      data: [
        {
          id: currentCart.id,
          version: currentCart.version,
        },
        { id: couponId, typeId: 'discount-code' },
        customQuery,
      ],
    });
    return { updatedCart: removeCartCouponResponse?.data?.cart };
  };

  return {
    load,
    addItem,
    removeItem,
    updateItemQty,
    clear,
    applyCoupon,
    removeCoupon,
  };
}
